import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FiCheck, FiPlus, FiSend, FiTrash2, FiUsers } from 'react-icons/fi';

const STATUS = [['todo', 'To do'], ['doing', 'In progress'], ['done', 'Done']];
const PRIORITY = { high: 'High', normal: 'Normal', low: 'Low' };

const formatDue = (value) => (value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');
const initialOf = (name) => String(name || 'T').trim().slice(0, 1).toUpperCase();

export function useTeamTasks(apiBase, { assigneeId } = {}) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/api/team/tasks`, { withCredentials: true, params: assigneeId ? { assigneeId } : {} });
      setTasks(data.tasks || []);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Tasks could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, assigneeId]);
  useEffect(() => { load(); }, [load]);
  return { tasks, setTasks, loading, error, reload: load };
}

function TaskRow({ task, canManage, showAssignee, onStatus, onDelete }) {
  return (
    <article className={`team-task ${task.status}${task.overdue ? ' overdue' : ''}`}>
      <button type="button" className="team-task-check" aria-label={task.status === 'done' ? 'Mark as to do' : 'Mark as done'} onClick={() => onStatus(task, task.status === 'done' ? 'todo' : 'done')}>{task.status === 'done' && <FiCheck aria-hidden="true" />}</button>
      <div className="team-task-body">
        <strong>{task.title}</strong>
        {task.notes && <p>{task.notes}</p>}
        <span className="team-task-meta">
          {showAssignee && task.assigneeName && <em>{task.assigneeName}</em>}
          <i className={`priority ${task.priority}`}>{PRIORITY[task.priority]}</i>
          {task.dueAt && <i className={task.overdue ? 'due overdue' : 'due'}>{task.overdue ? 'Overdue · ' : 'Due '}{formatDue(task.dueAt)}</i>}
          {task.workspaceName && <i>{task.workspaceName}</i>}
        </span>
      </div>
      <select value={task.status} aria-label={`Status of ${task.title}`} onChange={(event) => onStatus(task, event.target.value)}>
        {STATUS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      {canManage && <button type="button" className="icon-btn danger-icon" aria-label={`Delete task ${task.title}`} title="Delete task" onClick={() => onDelete(task)}><FiTrash2 /></button>}
    </article>
  );
}

// Used by owners (assign + manage) and by employees (see + update their own tasks).
export function TeamTaskList({ apiBase, tasks, setTasks, assignee, workspaces = [], canManage, showAssignee = false, setNotice, emptyText }) {
  const [draft, setDraft] = useState({ title: '', notes: '', priority: 'normal', dueAt: '', workspaceId: '' });
  const [saving, setSaving] = useState(false);
  const request = { withCredentials: true };

  const setStatus = async (task, status) => {
    const previous = tasks;
    setTasks((rows) => rows.map((row) => (row.id === task.id ? { ...row, status } : row)));
    try {
      const { data } = await axios.patch(`${apiBase}/api/team/tasks/${task.id}`, { status }, request);
      setTasks((rows) => rows.map((row) => (row.id === task.id ? data.task : row)));
    } catch (error) {
      setTasks(previous);
      setNotice?.({ type: 'error', message: error.response?.data?.error || 'Task could not be updated.' });
    }
  };
  const remove = async (task) => {
    if (!window.confirm(`Delete the task "${task.title}"?`)) return;
    try {
      await axios.delete(`${apiBase}/api/team/tasks/${task.id}`, request);
      setTasks((rows) => rows.filter((row) => row.id !== task.id));
    } catch (error) {
      setNotice?.({ type: 'error', message: error.response?.data?.error || 'Task could not be deleted.' });
    }
  };
  const assign = async (event) => {
    event.preventDefault();
    if (!assignee) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${apiBase}/api/team/tasks`, { ...draft, assigneeId: assignee.id, dueAt: draft.dueAt || undefined, workspaceId: draft.workspaceId || undefined }, request);
      setTasks((rows) => [data.task, ...rows]);
      setDraft({ title: '', notes: '', priority: 'normal', dueAt: '', workspaceId: '' });
      setNotice?.({ type: 'success', message: `Task assigned to ${assignee.name}.` });
    } catch (error) {
      setNotice?.({ type: 'error', message: error.response?.data?.error || 'Task could not be assigned.' });
    } finally {
      setSaving(false);
    }
  };

  const open = tasks.filter((task) => task.status !== 'done');
  const done = tasks.filter((task) => task.status === 'done');
  return (
    <div className="team-tasks">
      {canManage && assignee && (
        <form className="team-task-form" onSubmit={assign}>
          <label className="wide"><span>New task for {assignee.name}</span><input required maxLength={160} value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} placeholder="e.g. Prepare Diwali post for Easten Mediatech" /></label>
          <label className="wide"><span>Details (optional)</span><textarea rows="2" maxLength={2000} value={draft.notes} onChange={(event) => setDraft((current) => ({ ...current, notes: event.target.value }))} /></label>
          <label><span>Priority</span><select value={draft.priority} onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select></label>
          <label><span>Due date</span><input type="date" value={draft.dueAt} onChange={(event) => setDraft((current) => ({ ...current, dueAt: event.target.value }))} /></label>
          <label className="wide"><span>Workspace (optional)</span><select value={draft.workspaceId} onChange={(event) => setDraft((current) => ({ ...current, workspaceId: event.target.value }))}><option value="">Not tied to a workspace</option>{workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}</select></label>
          <button className="btn primary" type="submit" disabled={saving || !draft.title.trim()}><FiPlus aria-hidden="true" /> {saving ? 'Assigning' : 'Assign task'}</button>
        </form>
      )}
      {!tasks.length && <div className="empty-box compact-empty"><strong>No tasks yet</strong><small>{emptyText || 'Assigned tasks will show here.'}</small></div>}
      {open.length > 0 && <div className="team-task-group"><h4>Open · {open.length}</h4>{open.map((task) => <TaskRow key={task.id} task={task} canManage={canManage} showAssignee={showAssignee} onStatus={setStatus} onDelete={remove} />)}</div>}
      {done.length > 0 && <div className="team-task-group"><h4>Completed · {done.length}</h4>{done.map((task) => <TaskRow key={task.id} task={task} canManage={canManage} showAssignee={showAssignee} onStatus={setStatus} onDelete={remove} />)}</div>}
    </div>
  );
}

export function EmployeeTasks({ apiBase, setNotice }) {
  const { tasks, setTasks, loading, error } = useTeamTasks(apiBase);
  const open = tasks.filter((task) => task.status !== 'done').length;
  return (
    <section className="team-management employee-tasks">
      <header className="team-page-head"><div><h2>My tasks</h2><span>{loading ? 'Loading…' : `${open} open task${open === 1 ? '' : 's'} assigned to you`}</span></div></header>
      <div className="glass-panel team-employee-panel">
        {error ? <p className="settings-muted" role="alert">{error}</p> : <TeamTaskList apiBase={apiBase} tasks={tasks} setTasks={setTasks} canManage={false} setNotice={setNotice} emptyText="Your agency has not assigned you any task yet." />}
      </div>
    </section>
  );
}

export function TeamChat({ apiBase, setNotice }) {
  const [people, setPeople] = useState([]);
  const [active, setActive] = useState('team');
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const request = useMemo(() => ({ withCredentials: true }), []);

  useEffect(() => {
    axios.get(`${apiBase}/api/team/chat/contacts`, request).then(({ data }) => setPeople(data.people || [])).catch(() => setNotice?.({ type: 'error', message: 'Team members could not be loaded.' }));
  }, [apiBase, request, setNotice]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/api/team/chat`, { ...request, params: { with: active } });
      setMessages(data.messages || []);
    } catch (error) {
      setMessages([]);
    }
  }, [active, apiBase, request]);
  useEffect(() => {
    setMessages([]);
    loadMessages();
    const timer = window.setInterval(() => { if (!document.hidden) loadMessages(); }, 6000);
    return () => window.clearInterval(timer);
  }, [loadMessages]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages.length, active]);

  const send = async (event) => {
    event.preventDefault();
    const message = text.trim();
    if (!message || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${apiBase}/api/team/chat`, { with: active, message }, request);
      setMessages((rows) => [...rows, data.message]);
      setText('');
    } catch (error) {
      setNotice?.({ type: 'error', message: error.response?.data?.error || 'Message could not be sent.' });
    } finally {
      setSending(false);
    }
  };

  const activePerson = people.find((person) => person.id === active);
  return (
    <section className="team-chat" aria-label="Team chat">
      <aside className="team-chat-list">
        <h2>Team chat</h2>
        <button type="button" className={active === 'team' ? 'active' : ''} onClick={() => setActive('team')}><span className="team-chat-avatar group"><FiUsers aria-hidden="true" /></span><span><strong>Whole team</strong><small>Everyone in your agency</small></span></button>
        {people.map((person) => <button type="button" key={person.id} className={active === person.id ? 'active' : ''} onClick={() => setActive(person.id)}><span className="team-chat-avatar">{initialOf(person.name)}</span><span><strong>{person.name}</strong><small>{person.designation}</small></span></button>)}
        {!people.length && <p className="settings-muted">Invite employees from the Team page to chat with them here.</p>}
      </aside>
      <div className="team-chat-room">
        <header><strong>{active === 'team' ? 'Whole team' : activePerson?.name || 'Chat'}</strong><small>{active === 'team' ? 'Messages here are seen by everyone on your team.' : activePerson?.designation}</small></header>
        <div className="team-chat-messages" ref={listRef}>
          {!messages.length && <p className="team-chat-empty">No messages yet. Say hello.</p>}
          {messages.map((message) => (
            <div key={message.id} className={`team-chat-bubble ${message.mine ? 'mine' : ''}`}>
              {!message.mine && active === 'team' && <b>{message.senderName}</b>}
              <p>{message.message}</p>
              <small>{new Date(message.createdAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</small>
            </div>
          ))}
        </div>
        <form className="team-chat-compose" onSubmit={send}>
          <input value={text} maxLength={2000} onChange={(event) => setText(event.target.value)} placeholder="Write a message" aria-label="Write a message" />
          <button className="btn primary" type="submit" disabled={sending || !text.trim()}><FiSend aria-hidden="true" /> Send</button>
        </form>
      </div>
    </section>
  );
}
